import { FC, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { CheckCircleOutline, PeopleAltOutlined, ScienceOutlined, StarOutline } from "@mui/icons-material";

import PageTitle from "../../../components/PageTitle";
import { StatCard, useGatedNavigate } from "../../../components/ui";
import { SimpleSnackbar } from "../../../components/snackbar";
import { GiftButton } from "../../../components/gift/giftButton";
import { GiftCelebrationDialog } from "../../../components/gift/GiftCelebrationDialog";
import { useGiftCelebration } from "../../../components/gift/use-gift-celebration";
import { useAuth } from "../../../contexts/auth2";
import { handlePromise } from "../../../utils/handlePromisse";
import { getPlanType, getUserToken, refreshToken, removeUser, tokenToUser, updateUserToken } from "../../../services/userService";
import { ActivateGift, getEmailAndNotUsed } from "../../../services/gift.services";
import useDashboardData, { RecentConsultation } from "./use-dashboard-data";
import HeroGreeting from "./components/HeroGreeting";
import RecentConsultations from "./components/RecentConsultations";
import PlanCard from "./components/PlanCard";
import RankingCard from "./components/RankingCard";
import RenewalBanner from "./components/RenewalBanner";

// ----------------------------------------------------------------------

export const Dashboard: FC = () => {
    const navigate = useNavigate();
    const { user, setUser } = useAuth();
    const { navigateGated, gateDialogs } = useGatedNavigate();
    const { loading, error, profile, ranking, recentConsultations } = useDashboardData();

    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [hasGift, setHasGift] = useState(false);
    const [giftData, setGiftData] = useState<{ plan: string } | null>(null);
    const { celebrationOpen, openCelebration, closeCelebration } = useGiftCelebration(hasGift, giftData?.plan);

    const planType = getPlanType(user.plan) === "free" ? "free" : "paid";
    const displayName = profile?.name_User || (user.email ?? "").split("@")[0];

    const queriesLimit = profile?.amountQueries_PlansPG;
    const consultationsSublabel =
        planType === "free"
            ? undefined
            : queriesLimit && queriesLimit > 0
              ? `de ${queriesLimit} no plano`
              : "Plano ilimitado";

    const fetchGiftNotUsed = useCallback(async () => {
        const [result, giftError] = await handlePromise(getEmailAndNotUsed());
        if (giftError || !result.data) {
            setGiftData(null);
            setHasGift(false);
            return;
        }
        setGiftData(result.data);
        setHasGift(true);
    }, []);

    const activateUserGift = useCallback(async () => {
        const [result, giftError] = await handlePromise(ActivateGift());
        if (giftError) {
            setSnackbarMessage("Erro ao ativar presente.");
            setShowSnackbar(true);
            return;
        }
        if (result.data) {
            closeCelebration();
            setHasGift(false);
            refreshToken(getUserToken() ?? "").then((res) => {
                removeUser();
                // @ts-ignore
                updateUserToken(res.data.newToken);
                setUser(tokenToUser());
                setSnackbarMessage("Presente ativado com sucesso!");
                setShowSnackbar(true);
                navigate("/dashboard/app");
            });
        }
    }, [closeCelebration, navigate, setUser]);

    useEffect(() => {
        if (user?.email) fetchGiftNotUsed();
    }, [fetchGiftNotUsed, user?.email]);

    useEffect(() => {
        if (error) {
            setSnackbarMessage(error);
            setShowSnackbar(true);
        }
    }, [error]);

    const openConsultation = (item: RecentConsultation) => {
        const route = item.type === "ORAL" ? `/dashboard/reviewOral/${item.id}` : `/dashboard/reviewConsult/${item.id}`;
        navigateGated(route);
    };

    return (
        <Box sx={{ width: "100%", p: { xs: "18px 16px 24px", md: 4 } }}>
            <PageTitle title="Início" />
            {gateDialogs}

            <SimpleSnackbar show={showSnackbar} message={snackbarMessage} onDismiss={() => setShowSnackbar(false)} />

            <RenewalBanner planType={planType} endDate={profile?.dat_end_plan} onRenew={() => navigate("/dashboard/pricing")} />

            <HeroGreeting
                name={displayName}
                onStartConsult={() => navigateGated("/dashboard/query")}
                onHistory={() => navigateGated("/dashboard/history")}
            />

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: { xs: 1.5, md: 2.5 }, mb: 3 }}>
                <StatCard
                    icon={<StarOutline />}
                    value={ranking && ranking.position >= 1 ? `${ranking.position}º` : "—"}
                    label="Posição no ranking"
                    delta={
                        ranking?.positionDelta30d != null && ranking.positionDelta30d > 0
                            ? `↑ ${ranking.positionDelta30d} ${ranking.positionDelta30d === 1 ? "posição" : "posições"}`
                            : undefined
                    }
                    loading={loading}
                />
                <StatCard icon={<ScienceOutlined />} value={ranking?.formulas ?? "—"} label="Fórmulas montadas" loading={loading} />
                <StatCard icon={<PeopleAltOutlined />} value={ranking?.patients ?? "—"} label="Pacientes impactados" loading={loading} />
                <StatCard
                    icon={<CheckCircleOutline />}
                    value={profile?.consultationsThisMonth ?? profile?.amountQueriesUsed_User ?? "—"}
                    label="Consultas no mês"
                    sublabel={consultationsSublabel}
                    loading={loading}
                />
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.65fr 1fr" }, gap: 3, alignItems: "start" }}>
                <RecentConsultations
                    items={recentConsultations}
                    loading={loading}
                    onOpen={openConsultation}
                    onViewAll={() => navigateGated("/dashboard/history")}
                    onStart={() => navigateGated("/dashboard/query")}
                />
                <Box sx={{ display: "grid", gap: 3 }}>
                    <PlanCard
                        planType={planType}
                        planName={profile?.planName ?? null}
                        endDate={profile?.dat_end_plan}
                        onManage={() => navigate("/dashboard/pricing")}
                    />
                    <RankingCard
                        position={ranking?.position}
                        percentileAhead={ranking?.percentileAhead}
                        formulasToNextTier={ranking?.formulasToNextTier}
                        nextTierLabel={ranking?.nextTierLabel}
                    />
                </Box>
            </Box>

            <GiftButton hasGift={hasGift} onActivateGift={openCelebration} />

            <GiftCelebrationDialog
                open={celebrationOpen}
                planName={giftData?.plan}
                onActivate={() => activateUserGift()}
                onClose={closeCelebration}
            />
        </Box>
    );
};
